import type {AgentCcpEventName} from './api'
import {enqueueCcpEvent} from './telemetryOutbox'
import {getAgentTelemetryRuntimeConfig} from './runtimeConfig'

const DETAIL_MAX = 200
const INTERPRETER_END_MS = 20_000

let armed = false
let lastRefreshAt = 0
let interpreterEndingAt = 0
let wsAttached = false
const holdStateByContact = new Map<string, string>()

/** Master tab + CCP logged in. Events before this are ignored. */
export function setCcpTelemetryArmed(next: boolean): void {
  armed = next
  if (!next) holdStateByContact.clear()
}

export function noteCcpAgentRefresh(atMs = Date.now()): void {
  if (Number.isFinite(atMs) && atMs > 0) lastRefreshAt = atMs
}

/** Ms since the last agent.onRefresh. Null until the first snapshot. */
export function getCcpSnapshotAgeMs(now = Date.now()): number | null {
  if (!lastRefreshAt) return null
  return Math.max(0, now - lastRefreshAt)
}

/** The interpreter (or a network-drop auto-end) is about to end the contact. */
export function noteInterpreterEndingCall(): void {
  interpreterEndingAt = Date.now()
}

function consumeEndedBy(): string | undefined {
  if (!interpreterEndingAt) return undefined
  const recent = Date.now() - interpreterEndingAt <= INTERPRETER_END_MS
  interpreterEndingAt = 0
  return recent ? 'interpreter' : undefined
}

export function reportCcpEvent(
  event: AgentCcpEventName,
  opts?: {contactId?: string | null; detail?: string | null; atMs?: number},
): void {
  try {
    if (!armed) return
    if (!getAgentTelemetryRuntimeConfig()?.enabled) return
    if ((event === 'tab_hidden' || event === 'tab_visible') && !opts?.contactId) return
    const detail = truncate(opts?.detail, DETAIL_MAX)
    enqueueCcpEvent({
      event,
      atMs: opts?.atMs && opts.atMs > 0 ? opts.atMs : Date.now(),
      contactId: opts?.contactId || null,
      detail,
    })
  } catch {
    // Telemetry must not affect call handling.
  }
}

export function reportContactConnected(contactId: string): void {
  reportCcpEvent('contact_connected', {contactId})
}

export function reportContactEnded(contactId: string): void {
  reportCcpEvent('contact_ended', {contactId, detail: consumeEndedBy()})
}

/** Agent-connection state on the active contact. Emits hold/resume only on a change. */
export function observeAgentConnectionState(contactId: string, state: string | null): void {
  try {
    if (!contactId) return
    const next = (state ?? '').trim().toLowerCase()
    const prev = holdStateByContact.get(contactId) ?? ''
    if (next) holdStateByContact.set(contactId, next)
    else holdStateByContact.delete(contactId)
    if (next === 'hold' && prev !== 'hold') reportCcpEvent('hold', {contactId})
    else if (prev === 'hold' && next === 'connected') reportCcpEvent('resume', {contactId})
  } catch {
    // Telemetry must not affect call handling.
  }
}

export function clearAgentConnectionState(contactId: string): void {
  if (contactId) holdStateByContact.delete(contactId)
}

export function resetCcpWebSocketTelemetry(): void {
  wsAttached = false
}

type WsManager = {
  onConnectionLost?: (cb: () => void) => void
  onConnectionGain?: (cb: () => void) => void
}

export function attachCcpWebSocketTelemetry(getManager: () => WsManager | null | undefined): void {
  try {
    if (wsAttached) return
    const mgr = getManager()
    if (typeof mgr?.onConnectionLost !== 'function' || typeof mgr?.onConnectionGain !== 'function') {
      return
    }
    wsAttached = true
    mgr.onConnectionLost(() => {
      try {
        reportCcpEvent('ccp_ws_lost')
      } catch {
        /* ignore */
      }
    })
    mgr.onConnectionGain(() => {
      try {
        reportCcpEvent('ccp_ws_restored')
      } catch {
        /* ignore */
      }
    })
  } catch {
    /* ignore */
  }
}

export async function audioDeviceChangeDetail(): Promise<string | null> {
  try {
    const devices = await navigator.mediaDevices?.enumerateDevices?.()
    if (!devices?.length) return null
    const label = (kind: MediaDeviceKind) =>
      devices
        .filter((device) => device.kind === kind && device.label)
        .map((device) => device.label)
        .join('|')
    const text = `in=${label('audioinput')}; out=${label('audiooutput')}`
    return truncate(text, DETAIL_MAX)
  } catch {
    return null
  }
}

function truncate(value: string | null | undefined, max: number): string | null {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return null
  return trimmed.length <= max ? trimmed : trimmed.slice(0, max)
}
